
import { Component, Prop, Vue } from "vue-property-decorator";
import { Portal, PortalTarget } from "portal-vue";

@Component({
    name: "iv-portal",
    inheritAttrs: false,
})
export default class IvPortal extends Vue {
    /** 作為 Portal（發送端）時：要傳送到的 target 名稱 */
    @Prop({ type: String, required: false })
    readonly to!: string | undefined;

    /** 作為 PortalTarget（接收端）時：接收自哪一個名稱（對應原生的 name） */
    @Prop({ type: String, required: false })
    readonly name!: string | undefined;

    /**
     * 沒有 `to` / `name` 時是否改成本地 inline 渲染：
     * - false（預設）：不顯示
     * - true：在 iv-portal 位置直接 render slot 內容
     */
    @Prop({ type: Boolean, default: false })
    readonly inline!: boolean;

    /**
     * 只在「本地 inline 渲染」時使用：
     * - 有多個 children 或你希望強制包一層時，作為 wrapper 的 tag
     * - 若未設定且只有一個 child，會直接回傳 child（不多包一層）
     */
    @Prop({ type: String, required: false })
    readonly tag!: string | undefined;

    render(h: typeof Vue.prototype.$createElement) {
        const children = this.$slots.default || [];

        // 1) 有 name：扮演 PortalTarget（接收端）
        if (this.name) {
            return h(
                PortalTarget as any,
                {
                    props: {
                        name: this.name, // ⭐ 對應原生 PortalTarget 的 name
                    },
                    attrs: this.$attrs,
                    on: this.$listeners,
                    scopedSlots: this.$scopedSlots,
                },
                children,
            );
        }

        // 2) 沒有 name → 扮演 Portal（發送端）
        //    如果也沒有 to，就決定要不要本地 inline
        if (!this.to) {
            if (!this.inline) {
                // 不顯示
                return null;
            }

            // inline = true → 本地渲染 slot
            if (children.length === 0) {
                return null;
            }

            // 只有一個 child 且沒指定 tag：直接回傳 child，不多包一層
            if (!this.tag && children.length === 1) {
                return children[0] as any;
            }

            // 多個 child 或有指定 tag：包一層 wrapper
            return h(
                this.tag || "div",
                {
                    attrs: this.$attrs,
                    on: this.$listeners,
                },
                children,
            );
        }

        // 3) 有 to：正常使用 Portal（發送端）
        return h(
            Portal as any,
            {
                props: {
                    to: this.to,
                },
                attrs: this.$attrs,
                on: this.$listeners,
                scopedSlots: this.$scopedSlots,
            },
            children,
        );
    }
}
